import React, {useEffect, useMemo, useState} from 'react';
import PropTypes from 'prop-types';
import {Space} from 'antd';
import {useTranslation} from 'react-i18next';
import {useSelector} from 'react-redux';
import {
  StyledExpenseDetailsTab,
  StyledExpenseDetailsTabSpace,
  StyledExpenseDetailsTabPrice,
  StyledExpenseDetailsTabCollapse
} from './StyledExpenseDetailsTab';
import ExpenseDetails from '../tabComponents/ExpenseDetails';
import ExpensePolicyIcon from '../../ExpensePolicyIcon';
import IconWrapper from '../../../../IconWrapper';
import ExpenseCategoryIcon from '../../ExpenseCategoryIcon';
import TransactionDetails from '../tabComponents/TransactionDetails';
import InvoiceDetailsPanel from '../../../TransactionsPage/InvoiceDetailsPanel/InvoiceDetailsPanel';
import SubscriptionDetailsPanel from '../../SubscriptionDetailsPanel';
import {StyledBudgetPreviewOverBudgetTag} from '../../../InvoicePage/BudgetPreview/StyledBudgetPreview';
import {ChevronRightIcon} from '../../../../../icons';
import {
  amountHelpers,
  dateHelpers,
  expensesHelpers,
  subscriptionsHelpers,
  transactionsHelpers,
  vendorsHelpers,
} from '../../../../../utils/helpers';
import {expensePolicyStatusConstants} from '../../../../../constants';
import {useIsAdvancedExpensesPlan} from '../../../../../hooks';


const ExpenseDetailsTab = ({
  defaultActivePanelKey = ['expense'],
  expense,
  onEdit,
  onInvoiceRemove,
  onSubscriptionUpdate,
  onUpdate,
  subscription = null,
  transaction = null,
  ...rest
}) => {
  const [t] = useTranslation(['main', 'expenses']);

  const isAdvancedExpensesPlan = useIsAdvancedExpensesPlan();

  const date = useMemo(() => {
    const format = 'DD MMM YYYY, HH:mm';
    return expense?.created_at
      ? dateHelpers.getDateFromISO(expense.created_at, format)
      : transactionsHelpers.getTransactionDate(transaction, format);
  }, [expense?.created_at, transaction]);

  const getAmount = () => {
    if (expense) return expensesHelpers.getExpensePrice(expense);

    const isIncoming = transaction?.is_incoming || false;
    const amount = transaction?.amount || 0;

    return amountHelpers.getAmountWithCurrencyCode(isIncoming ? amount : amount > 0 ? -amount : amount);
  }

  const amount = getAmount();

  const { employeeEmail, vendors } = useSelector(({ vendor, user }) => ({
    employeeEmail: user?.employee?.email,
    vendors: vendor.vendors
  }));

  const vendor = useMemo(() => {
    const vendorId = expense?.vendor_local_id;
    if (!vendorId) return null;
    const vendor = vendors.find(v => v.id === vendorId);
    if (vendor) return vendor;
    return { vendor_id: vendorId, name: expense?.vendor_local_name };
  }, [expense?.vendor_local_id, vendors]); // eslint-disable-line react-hooks/exhaustive-deps

  const isOverBudget = useMemo(() => {
    if (!subscription || !expense) return false;

    return subscriptionsHelpers.isSubscriptionOverBudget({expense, subscription});
  }, [expense, subscription]);

  const isOutOfPolicy = useMemo(() => expense?.policy_status === expensePolicyStatusConstants.OUT_OF_POLICY, [expense?.policy_status]);

  const enableInvoiceSection = useMemo(() => {
    if (!isAdvancedExpensesPlan) return true;

    const isExpenseReportedByEmployee = expense?.reported_by?.email === employeeEmail;
    const isDraft = expensesHelpers.isExpenseDraft(expense);
    return !(isDraft && isExpenseReportedByEmployee);
  }, [employeeEmail, expense, isAdvancedExpensesPlan]);

  const expensePanelExtra = (
    <Space size='small' align='center' className='d-flex-child'>
      {isOutOfPolicy && <ExpensePolicyIcon status={expense?.policy_status} />}
      {expensesHelpers.getExpenseStatusLabel(expense, t)}
    </Space>
  );

  const getLabel = (panel) => t(panel);

  const collapseItems = [
    transaction && {
      key: 'transaction',
      label: getLabel('transaction'),
      children: <TransactionDetails transaction={transaction} />,
      extra: transactionsHelpers.getTransactionDetailsRightContent({ transaction, t })
    },
    enableInvoiceSection && {
      className: 'expense-invoice-details-panel',
      collapsible: 'icon',
      key: 'invoice',
      children: (
        <InvoiceDetailsPanel
          displayMissingFieldIndicator={true}
          expense={expense}
          onExpenseUpdate={onUpdate}
          onInvoiceRemove={onInvoiceRemove}
        />
      ),
      showArrow: false,
    },
    subscription && {
      key: 'budget',
      label: getLabel('Budget'),
      children: (
        <SubscriptionDetailsPanel
          expense={expense}
          subscription={subscription}
          onExpenseUpdate={onUpdate}
          onSubscriptionUpdate={onSubscriptionUpdate}
        />
      ),
      extra: isOverBudget && <StyledBudgetPreviewOverBudgetTag>{t('overbudget')}</StyledBudgetPreviewOverBudgetTag>
    },
    {
      key: 'expense',
      label: getLabel('expense'),
      children: (
        <ExpenseDetails
          displayMissingFieldIndicator={true}
          expense={expense}
          onEdit={onEdit}
          subscription={subscription}
        />
      ),
      extra: expensePanelExtra
    },
  ].filter(Boolean);

  const openedPanelKeys = useMemo(() => {
    const availableKeys = collapseItems.map(item => item.key);
    const requestedKeys = [...defaultActivePanelKey, 'invoice', isOverBudget && 'budget']
      .filter(Boolean);
    const uniqueKeys = Array.from(new Set(requestedKeys));
    return uniqueKeys.filter(key => availableKeys.includes(key));
  }, [defaultActivePanelKey, isOverBudget]); // eslint-disable-line react-hooks/exhaustive-deps

  const [activePanelKeys, setActivePanelKeys] = useState(openedPanelKeys);

  useEffect(() => {
    setActivePanelKeys(openedPanelKeys);
  }, [openedPanelKeys]);

  const headerTitle = vendor ? vendorsHelpers.getVendorNameWithLink(vendor) : expense?.description;

  return (
    <StyledExpenseDetailsTab {...rest}>
      <StyledExpenseDetailsTabSpace direction='vertical' size='large'>
        <div className='d-flex justify-space-between align-items-end'>
          <Space size='small' direction='vertical'>
            {headerTitle}
            <Space size={4} direction='vertical'>
              <StyledExpenseDetailsTabPrice>{amount}</StyledExpenseDetailsTabPrice>
              {date}
            </Space>
          </Space>
          <ExpenseCategoryIcon expense={transaction || expense} />
        </div>

        <StyledExpenseDetailsTabCollapse
          bordered={false}
          activeKey={activePanelKeys}
          onChange={setActivePanelKeys}
          items={collapseItems}
          expandIcon={({ isActive }) => <IconWrapper size={20} rotate={isActive ? 90 : 0}><ChevronRightIcon /></IconWrapper>}
        />
      </StyledExpenseDetailsTabSpace>

    </StyledExpenseDetailsTab>
  );
}

ExpenseDetailsTab.propTypes = {
  defaultActivePanelKey: PropTypes.arrayOf(PropTypes.string),
  expense: PropTypes.object,
  onApprove: PropTypes.func,
  onDelete: PropTypes.func,
  onEdit: PropTypes.func,
  onInvoiceRemove: PropTypes.func,
  onReject: PropTypes.func,
  onSubscriptionUpdate: PropTypes.func,
  onUpdate: PropTypes.func,
  subscription: PropTypes.object,
  transaction: PropTypes.object,
}

export default ExpenseDetailsTab;
